import { useState } from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { ArrowCycle, Hub } from '@factorialco/f0-react/icons/app'
import { ComplaintsDataCollectionItemFragment } from 'generated/resources/graphql'

import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ChangeCategoryModal from 'modules/complaints/components/Complaints/Complaint/ComplaintIdAndActions/ChangeCategoryModal'
import { useComplaintStatus } from 'modules/complaints/hooks/useComplaintStatus'
import { getStatusColor, statusLabel } from 'modules/complaints/lib/utils/complaintStatus'
import { ChangeStatusModal } from 'modules/complaints/pages/ComplaintsPage/ChangeStatusModal'

interface Props {
  complaint: ComplaintsDataCollectionItemFragment
}

const ComplaintHeader: React.FC<Props> = ({ complaint }) => {
  const [changeStatusModal, setChangeStatusModal] = useState<{
    isOpen: boolean
    complaint: ComplaintsDataCollectionItemFragment | null
  }>({
    isOpen: false,
    complaint: null,
  })
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)

  const { getStatusOptions } = useComplaintStatus()

  const subject =
    complaint.identifier || i18n.t('complaints.overview.complaint', { complaintId: complaint.id })
  const statusColor = getStatusColor(complaint.status)
  const statusOptions = getStatusOptions(complaint)

  const primaryAction = {
    label: i18n.t('complaints.overview.change_status'),
    icon: ArrowCycle,
    onClick: () => setChangeStatusModal({ isOpen: true, complaint }),
    disabled: statusOptions.length === 0,
    tooltip: statusOptions.length === 0 ? i18n.t('complaints.header.no_status_options') : undefined,
  }

  const secondaryActions = [
    {
      label: i18n.t('complaints.overview.change_category'),
      icon: Hub,
      onClick: () => setCategoryModalOpen(true),
      disabled: complaint.blocked,
      tooltip: complaint.blocked ? i18n.t('complaints.header.blocked_complaint') : undefined,
    },
  ]

  return (
    <>
      <ResourceHeader
        title={subject}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        status={{
          label: i18n.t('complaints.overview.status'),
          text: statusLabel(complaint.status),
          variant: statusColor,
          actions:
            statusOptions.length > 0
              ? [
                  {
                    icon: ArrowCycle,
                    label: i18n.t('complaints.overview.change_status'),
                    onClick: () => setChangeStatusModal({ isOpen: true, complaint }),
                  },
                ]
              : undefined,
        }}
        metadata={[
          {
            label: i18n.t('complaints.overview.category'),
            value: {
              type: 'dot-tag',
              label: complaint.category?.name || i18n.t('complaints.category.none'),
              color: 'smoke',
            },
            actions: complaint.blocked
              ? undefined
              : [
                  {
                    label: i18n.t('complaints.overview.change_category'),
                    icon: Hub,
                    onClick: () => setCategoryModalOpen(true),
                  },
                ],
          },
          {
            label: i18n.t('complaints.overview.submitted_by'),
            value: {
              type: 'text',
              content: complaint.complainerName || i18n.t('complaints.overview.unknown'),
            },
          },
          {
            label: i18n.t('complaints.overview.relationship'),
            value: {
              type: 'text',
              content: complaint.complainerRelationship || i18n.t('complaints.overview.unknown'),
            },
          },
          {
            label: i18n.t('complaints.overview.due_date'),
            value: {
              type: 'text',
              content: complaint.acknowledgeDueAt
                ? convertDate(
                    complaint.acknowledgeDueAt,
                    'backend_datetime',
                    'ui_abbreviated_month_year'
                  )
                : i18n.t('complaints.overview.unknown'),
            },
          },
        ]}
      />

      {changeStatusModal.complaint && (
        <ChangeStatusModal
          isOpen={changeStatusModal.isOpen}
          onClose={() => setChangeStatusModal({ isOpen: false, complaint: null })}
          complaint={changeStatusModal.complaint}
        />
      )}
      <ChangeCategoryModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        complaint={complaint}
      />
    </>
  )
}

export default ComplaintHeader
